/**
 * CriterionRow — single sortable row inside CriteriaPanel.
 *
 * Inline-editable name + category + weight. Each field has a 600ms debounce
 * before dispatching `updateCriterion`. The row also exposes a drag handle
 * (desktop) or up/down arrows (mobile), and a delete button.
 *
 * Linear: MYD-208 (E3).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ChevronUp, ChevronDown, Trash2, Edit3 } from 'lucide-react'
import { Input } from 'components/ui/input'
import { cn } from 'lib/utils'

import { updateCriterion, deleteCriterion } from 'slices/scoringSlice'
import WeightBar from './WeightBar'

const DEBOUNCE_MS = 600

interface CriterionRowProps {
	criterion: any
	evalId: string
	totalWeight: number
	index: number
	count: number
	disableDrag: boolean
	onMoveUp: () => void
	onMoveDown: () => void
}

const CriterionRow = ({
	criterion,
	evalId,
	totalWeight,
	index,
	count,
	disableDrag,
	onMoveUp,
	onMoveDown,
}: CriterionRowProps) => {
	const dispatch = useDispatch()

	// Local editable state — synced from the criterion prop unless the user is
	// mid-edit (lastCommittedRef pattern, same as ScoreEntrySection).
	const [name, setName] = useState<string>(criterion.name ?? '')
	const [category, setCategory] = useState<string>(criterion.category ?? '')
	const [weight, setWeight] = useState<string>(criterion.weight == null ? '' : String(criterion.weight))

	const lastSavedRef = useRef({
		name: criterion.name ?? '',
		category: criterion.category ?? '',
		weight: criterion.weight == null ? '' : String(criterion.weight),
	})

	useEffect(() => {
		// External update (e.g. fetch refresh, slice merge) — only adopt if it
		// differs from what we've already saved locally. Prevents a race where
		// an in-flight update.fulfilled clobbers a fresh keystroke.
		const next = {
			name: criterion.name ?? '',
			category: criterion.category ?? '',
			weight: criterion.weight == null ? '' : String(criterion.weight),
		}
		if (next.name !== lastSavedRef.current.name) {
			setName(next.name)
			lastSavedRef.current.name = next.name
		}
		if (next.category !== lastSavedRef.current.category) {
			setCategory(next.category)
			lastSavedRef.current.category = next.category
		}
		if (next.weight !== lastSavedRef.current.weight) {
			setWeight(next.weight)
			lastSavedRef.current.weight = next.weight
		}
	}, [criterion._id, criterion.name, criterion.category, criterion.weight])

	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const flushUpdate = useCallback(() => {
		const payload: any = {}
		const trimmedName = name.trim()
		if (trimmedName !== lastSavedRef.current.name && trimmedName.length > 0) {
			payload.name = trimmedName
		}
		const trimmedCategory = category.trim()
		if (trimmedCategory !== lastSavedRef.current.category) {
			payload.category = trimmedCategory || undefined
		}
		if (weight !== lastSavedRef.current.weight) {
			const w = Number(weight)
			if (Number.isFinite(w) && w >= 0 && w <= 100) {
				payload.weight = w
			}
		}
		if (Object.keys(payload).length === 0) return

		// @ts-ignore — thunks
		dispatch(updateCriterion({ evalId, criterionId: criterion._id, payload }))
		if (payload.name) lastSavedRef.current.name = payload.name
		if ('category' in payload) lastSavedRef.current.category = payload.category ?? ''
		if (payload.weight != null) lastSavedRef.current.weight = String(payload.weight)
	}, [dispatch, evalId, criterion._id, name, category, weight])

	const scheduleUpdate = useCallback(() => {
		if (timerRef.current) clearTimeout(timerRef.current)
		timerRef.current = setTimeout(flushUpdate, DEBOUNCE_MS)
	}, [flushUpdate])

	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current)
		}
	}, [])

	const handleBlur = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current)
			timerRef.current = null
		}
		flushUpdate()
	}, [flushUpdate])

	const handleDelete = useCallback(async () => {
		const ok = await toast.confirm({
			title: 'Delete criterion',
			message: `Delete criterion "${criterion.name}"? Associated scores will be orphaned.`,
		})
		if (!ok) return
		// @ts-ignore — thunks
		dispatch(deleteCriterion({ evalId, criterionId: criterion._id }))
	}, [dispatch, evalId, criterion._id, criterion.name])

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: criterion._id,
		disabled: disableDrag,
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				'flex items-start gap-2 px-2 py-2 rounded-md border bg-white',
				isDragging ? 'border-[#0080fe] shadow-md' : 'border-gray-200 hover:border-gray-300'
			)}
		>
			{/* Drag handle / mobile arrows */}
			{!disableDrag ? (
				<button
					type="button"
					{...attributes}
					{...listeners}
					aria-label={`Drag criterion ${name || index + 1}`}
					className="p-1 mt-0.5 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing touch-none shrink-0"
				>
					<GripVertical className="w-4 h-4" />
				</button>
			) : (
				<div className="flex flex-col gap-0.5 mt-0.5 shrink-0">
					<button
						type="button"
						onClick={onMoveUp}
						disabled={index === 0}
						aria-label={`Move ${name || 'criterion'} up`}
						className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
					>
						<ChevronUp className="w-3.5 h-3.5" />
					</button>
					<button
						type="button"
						onClick={onMoveDown}
						disabled={index === count - 1}
						aria-label={`Move ${name || 'criterion'} down`}
						className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
					>
						<ChevronDown className="w-3.5 h-3.5" />
					</button>
				</div>
			)}

			{/* Inline-editable fields */}
			<div className="flex-1 min-w-0 space-y-1.5">
				<div className="flex items-center gap-1.5">
					<Edit3 className="w-3 h-3 text-gray-300 shrink-0" aria-hidden />
					<input
						type="text"
						value={name}
						onChange={e => {
							setName(e.target.value)
							scheduleUpdate()
						}}
						onBlur={handleBlur}
						placeholder="Criterion name"
						className="flex-1 min-w-0 text-sm font-medium text-gray-900 bg-transparent border-0 border-b border-transparent focus:border-[#0080fe] focus:outline-none px-0 py-0.5"
					/>
				</div>
				<div className="flex items-center gap-2">
					<input
						type="text"
						value={category}
						onChange={e => {
							setCategory(e.target.value)
							scheduleUpdate()
						}}
						onBlur={handleBlur}
						placeholder="Category (optional)"
						className="flex-1 min-w-0 text-xs text-gray-500 bg-transparent border-0 border-b border-transparent focus:border-[#0080fe] focus:outline-none px-0 py-0.5"
					/>
					<div className="flex items-center gap-1 shrink-0">
						<Input
							type="number"
							min={0}
							max={100}
							step={1}
							value={weight}
							onChange={e => {
								setWeight(e.target.value)
								scheduleUpdate()
							}}
							onBlur={handleBlur}
							className="w-20 text-center text-sm h-7"
							aria-label="Weight"
						/>
						<span className="text-xs text-gray-500">%</span>
					</div>
				</div>
				<WeightBar weight={Number(weight) || 0} totalWeight={totalWeight} />
			</div>

			<button
				type="button"
				onClick={handleDelete}
				aria-label={`Delete criterion ${name}`}
				title="Delete criterion"
				className="p-1.5 mt-0.5 text-gray-400 hover:text-red-500 hover:bg-gray-50 rounded shrink-0"
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

export default CriterionRow
